import { ConfirmDialog } from '@/components/editor/dialogs/ConfirmDialog';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useDialogs } from '@/contexts/DialogContext';
import { useSelectionMode } from '@/contexts/SelectionModeContext';

export interface IndexConfirmDialogsProps {
  // Wrapped clear-handler from caller (does its own logging + reset).
  onClearConfirmed: () => void;
  clearLabel: string;
}

/**
 * Three destructive-action confirmation dialogs (clear canvas, delete block,
 * delete group). Reads open-flags from DialogContext, workflow data + raw
 * mutations from WorkflowContext, and selection from SelectionModeContext —
 * caller only needs to pass the wrapped clear-handler + i18n label.
 */
export function IndexConfirmDialogs({ onClearConfirmed, clearLabel }: IndexConfirmDialogsProps) {
  const { blocks, connectors, groups, selectedBlock, selectedBlockId, deleteBlock, deleteGroup } = useWorkflow();
  const { selectedGroupId } = useSelectionMode();
  const dlg = useDialogs();

  const selectedGroup = groups.find(g => g.id === selectedGroupId) || null;

  return (
    <>
      <ConfirmDialog
        open={dlg.confirmClearOpen}
        onOpenChange={dlg.setConfirmClearOpen}
        title={clearLabel + '?'}
        description={`This will remove all ${blocks.length} blocks, ${connectors.length} connectors, and ${groups.length} groups from the canvas. This action can be undone with Ctrl+Z.`}
        confirmLabel={clearLabel}
        onConfirm={onClearConfirmed}
      />
      <ConfirmDialog
        open={dlg.confirmDeleteBlockOpen}
        onOpenChange={dlg.setConfirmDeleteBlockOpen}
        title="Delete block?"
        description={selectedBlock ? `Delete "${selectedBlock.config.name}"? Connected lines will also be removed.` : 'Delete this block?'}
        confirmLabel="Delete"
        onConfirm={() => { if (selectedBlockId) deleteBlock(selectedBlockId); }}
      />
      <ConfirmDialog
        open={dlg.confirmDeleteGroupOpen}
        onOpenChange={dlg.setConfirmDeleteGroupOpen}
        title="Delete group?"
        description={selectedGroup ? `Delete group "${selectedGroup.name || 'Unnamed'}"? The blocks inside will be ungrouped but not deleted.` : 'Delete this group?'}
        confirmLabel="Delete"
        onConfirm={() => { if (selectedGroupId) deleteGroup(selectedGroupId); }}
      />
    </>
  );
}
